/*

& Search inside a conversation (3 Nov 2026, gap A9), the app's in-chat search: "Search" in the header's menu swaps the
& header for a "Search messages" field and the composer for a "3 of 12" bar with ⌃ (older) and ⌄ (newer). It searches
& the messages already loaded, as the app does, matching everything a reader can see in a row: the text, a file's name,
& a shared listing's title and place, a requirement's title, a location, a contact card. The newest hit is picked first
& (the thread sits at its foot), the arrows wrap at either end, and the picked row is tinted and brought into view.

*/

import { InputBase, IconButton, Typography } from "@mui/material";
import { CloseOutlined, KeyboardArrowDown, KeyboardArrowUp, SearchOutlined } from "@mui/icons-material";
import React from "react";

/* The app's highlight: the brand orange's tint, the match in bold (ChatSearchHighlight.kt). */
const HIGHLIGHT_STYLE: React.CSSProperties = { backgroundColor: "#FCE3CF", color: "#8A4210", fontWeight: 600, borderRadius: "2px" };
/* The active row's tint (ChatSearchActiveRowTint, 0x1FB05911). */
export const CHAT_SEARCH_ACTIVE_ROW_TINT: string = "rgba(176, 89, 17, 0.12)";

/** Everything a reader can see in one message's row; nothing for a deleted message or a system line. */
export const chatMessageSearchText = (message: any): string => {
	if (!message || message.is_deleted === true || message.message_type === "system") return "";
	const parts: Array<string> = [String(message.body || "")];
	for (const attachment of message.attachments || []) if (attachment?.file_name) parts.push(String(attachment.file_name));
	for (const listing of Array.isArray(message.attached_listings_details) ? message.attached_listings_details : []) {
		if (listing?.title) parts.push(String(listing.title));
		if (listing?.location_title) parts.push(String(listing.location_title));
	}
	if (message.requirement_details?.title) parts.push(String(message.requirement_details.title));
	if (message.message_type === "location" && message.location_details) {
		parts.push(String(message.location_details.title || ""), String(message.location_details.address || ""));
	}
	if (message.message_type === "contact" && message.contact_details) parts.push(String(message.contact_details.title || ""));
	return parts.join(" ");
};

/** Ids of the loaded messages containing [query], oldest first. A blank query matches nothing. */
export const chatSearchMatchIds = (messages: Array<any>, query: string): Array<string> => {
	const term: string = query.trim().toLowerCase();
	if (!term) return [];
	// The thread arrives newest first.
	return (messages || [])
		.slice()
		.reverse()
		.filter((message) => chatMessageSearchText(message).toLowerCase().includes(term))
		.map((message) => String(message._id));
};

/** [text] with every case-insensitive occurrence of [query] marked. */
export const highlightChatText = (text: string, query: string, keyPrefix: string = "h"): React.ReactNode => {
	const term: string = query.trim().toLowerCase();
	if (!term || !text) return text;
	const haystack: string = text.toLowerCase();
	const out: Array<React.ReactNode> = [];
	let from: number = 0;
	let at: number = haystack.indexOf(term, from);
	while (at >= 0) {
		if (at > from) out.push(text.slice(from, at));
		out.push(
			<mark
				key={`${keyPrefix}-${at}`}
				style={HIGHLIGHT_STYLE}
			>
				{text.slice(at, at + term.length)}
			</mark>,
		);
		from = at + term.length;
		at = haystack.indexOf(term, from);
	}
	if (from < text.length) out.push(text.slice(from));
	return out;
};

const tr = (locale: string | undefined, en: string, ar: string, kn: string): string =>
	locale === "ar-AE" ? ar : locale === "kn-IN" ? kn : en;

/** The header in search mode: the field and a close button. Escape closes it too. */
export const ChatSearchHeader = ({
	query,
	onQueryChange,
	onClose,
	locale,
}: {
	query: string;
	onQueryChange: (query: string) => void;
	onClose: () => void;
	locale: string | undefined;
}) => (
	<div
		style={{
			display: "flex",
			alignItems: "center",
			gap: "0.5rem",
			padding: "0.75rem 1rem",
			borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
			direction: locale === "ar-AE" ? "rtl" : "ltr",
		}}
	>
		<div
			style={{
				flex: 1,
				display: "flex",
				alignItems: "center",
				gap: "0.5rem",
				padding: "0.375rem 0.75rem",
				borderRadius: "999px",
				border: "1px solid rgba(0, 0, 0, 0.12)",
			}}
		>
			<SearchOutlined sx={{ fontSize: "1.2rem", color: "#6c757d" }} />
			<InputBase
				autoFocus
				fullWidth
				value={query}
				onChange={(event) => onQueryChange(event.target.value)}
				onKeyDown={(event) => event.key === "Escape" && onClose()}
				placeholder={tr(locale, "Search messages", "ابحث في الرسائل", "ಸಂದೇಶಗಳನ್ನು ಹುಡುಕಿ")}
				inputProps={{ "aria-label": tr(locale, "Search messages", "ابحث في الرسائل", "ಸಂದೇಶಗಳನ್ನು ಹುಡುಕಿ") }}
				sx={{ fontSize: "0.9rem" }}
			/>
		</div>
		<IconButton
			aria-label={tr(locale, "Close search", "إغلاق البحث", "ಹುಡುಕಾಟ ಮುಚ್ಚಿ")}
			onClick={onClose}
		>
			<CloseOutlined />
		</IconButton>
	</div>
);

/** The composer's place while searching: "3 of 12" (or "No matches") with ⌃ older and ⌄ newer. */
export const ChatSearchMatchBar = ({
	matchNumber,
	matchCount,
	onPrevious,
	onNext,
	locale,
}: {
	matchNumber: number;
	matchCount: number;
	onPrevious: () => void;
	onNext: () => void;
	locale: string | undefined;
}) => (
	<div
		style={{
			display: "flex",
			alignItems: "center",
			gap: "0.5rem",
			margin: "0.5rem 1rem 0.75rem",
			padding: "0.25rem 0.5rem 0.25rem 1.125rem",
			borderRadius: "999px",
			border: "1px solid rgba(0, 0, 0, 0.12)",
			direction: locale === "ar-AE" ? "rtl" : "ltr",
		}}
	>
		<Typography sx={{ flex: 1, fontSize: "0.85rem", fontWeight: 500, color: matchCount === 0 ? "text.secondary" : "text.primary" }}>
			{matchCount === 0
				? tr(locale, "No matches", "لا توجد نتائج", "ಯಾವುದೇ ಹೊಂದಾಣಿಕೆಗಳಿಲ್ಲ")
				: tr(locale, `${matchNumber} of ${matchCount}`, `${matchNumber} من ${matchCount}`, `${matchCount} ರಲ್ಲಿ ${matchNumber}`)}
		</Typography>
		<IconButton
			size="small"
			aria-label={tr(locale, "Previous match", "النتيجة السابقة", "ಹಿಂದಿನ ಹೊಂದಾಣಿಕೆ")}
			disabled={matchCount === 0}
			onClick={onPrevious}
			color="primary"
		>
			<KeyboardArrowUp />
		</IconButton>
		<IconButton
			size="small"
			aria-label={tr(locale, "Next match", "النتيجة التالية", "ಮುಂದಿನ ಹೊಂದಾಣಿಕೆ")}
			disabled={matchCount === 0}
			onClick={onNext}
			color="primary"
		>
			<KeyboardArrowDown />
		</IconButton>
	</div>
);
